

import React from 'react'
import gql from "graphql-tag"
import c from 'classnames'
import { Tooltip } from 'antd'

import { showNotification } from '@/state/user-interface'
import { apolloClient } from '@/state/apollo-client'
import { useAppDispatch } from '@/utils/hooks'
import iconStyle from '@sass/common/icon.module.sass'
import style from './team-item.module.sass'
import { useCreateUserTeamMutation, useRemoveUserTeamMutation } from './__gen/team-item'
import { UserToManageQuery } from '../__gen/edit'
import { TeamsToAssignQuery } from './__gen'

type UserType = NonNullable<UserToManageQuery["user"]>
type TeamType = NonNullable<TeamsToAssignQuery["team_List"]>["objects"][number]


gql`
	mutation createUserTeam(
		$team: ID!,
		$user: ID!,
	) {
		userTeam_Update(input: {
			team: $team,
			user: $user
		}) {
			object {
				id
				active
				team {
					id
				}
				user {
					id
				}
			}
		}
	}
`

gql`
	mutation removeUserTeam(
		$id: ID!
	) {
		userTeam_Update(input: {
			id: $id,
			active: false
		}) {
			object {
				id
				active
			}
		}
	}
`

type Props = {
	editable: boolean
	// Set when access is granted by a user module, so it can't be changed here
	lockedReason: string | null
	team: TeamType
	user: UserType
}


export default (props: Props) => {
	const [remove, removeDetails] = useRemoveUserTeamMutation()
	const [add, addDetails] = useCreateUserTeamMutation()
	const loading = removeDetails.loading || addDetails.loading
	const dispatch = useAppDispatch()

	const updateCache = (memberId: string | null) => {
		apolloClient.writeFragment({
			id: 'TeamType:' + props.team.id,
			fragment: gql`
				fragment _ on TeamType {
					memberId(userId: "${props.user.id}")
				}
			`,
			data: {
				memberId,
				__typename: 'TeamType'
			},
		})
	}

	const locked = !!props.lockedReason
	const selected = locked || !!props.team.memberId
	const interactive = props.editable && !locked && !loading

	const toggle = async () => {
		if (!interactive) return
		if (props.team.memberId) {
			await remove({variables: {id: props.team.memberId}})
			dispatch(showNotification({
				content: `Removed ${props.user.firstName} from ${props.team.name}`,
			}))
			updateCache(null)
		} else {
			const r = await add({variables: {user: props.user.id, team: props.team.id}})
			dispatch(showNotification({
				content: `Added ${props.user.firstName} to ${props.team.name}`,
			}))
			updateCache(r.data!.userTeam_Update!.object!.id)
		}
	}

	const chip = (
		<button
			type="button"
			aria-pressed={selected}
			disabled={!interactive}
			className={c(style.chip, {
				[style.selected]: selected,
				[style.interactive]: interactive,
				// Only grey out in edit mode, otherwise it reads like any other team they can access
				[style.locked]: locked && props.editable,
			})}
			onClick={toggle}
		>
			<span className={c(style.mark, {
				[style.spinner]: loading,
				// Locked teams get a grey tick (via .locked) so they still read as having access
				[iconStyle.tick]: selected && !loading,
			})} />
			{props.team.name}
		</button>
	)

	return props.lockedReason && props.editable
		? <Tooltip title={props.lockedReason}><span>{chip}</span></Tooltip>
		: chip
}
